import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ManagerLayout from './ManagerLayout';
import { Search, RefreshCw, Send } from 'lucide-react';

const STATUS_OPTIONS = [
  { v: 'open', l: 'Open', c: '#4F8EF7' },
  { v: 'in_progress', l: 'In Progress', c: '#7C3AED' },
  { v: 'waiting_company', l: 'Waiting Company', c: '#F59E0B' },
  { v: 'resolved', l: 'Resolved', c: '#10B981' },
  { v: 'closed', l: 'Closed', c: '#94A3B8' },
];

export default function ManagerTickets() {
  const [manager, setManager] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: mgr } = await supabase
        .from('managers').select('*')
        .eq('auth_user_id', user.id).maybeSingle();

      if (!mgr) { setLoading(false); return; }
      setManager(mgr);

      const { data } = await supabase
        .from('support_tickets').select('*')
        .eq('manager_id', mgr.id)
        .order('updated_at', { ascending: false });

      setTickets(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelected(ticket);
    const { data } = await supabase
      .from('ticket_messages').select('*')
      .eq('ticket_id', ticket.id)
      .order('created_at', { ascending: true });
    setMessages(data || []);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || !manager) return;
    setSending(true);
    try {
      await supabase.from('ticket_messages').insert({
        ticket_id: selected.id,
        sender_type: 'manager',
        sender_name: manager.name,
        sender_id: manager.id,
        message: reply.trim(),
      });
      await supabase.from('support_tickets').update({
        status: 'waiting_company',
        manager_response: reply.trim(),
        manager_responded_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', selected.id);

      setReply('');
      await openTicket({ ...selected, status: 'waiting_company' });
      setSelected(prev => ({ ...prev, status: 'waiting_company' }));
      await load();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    await supabase.from('support_tickets').update({
      status,
      resolved_at: status === 'resolved' ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }).eq('id', ticketId);
    setSelected(prev => prev ? { ...prev, status } : null);
    await load();
  };

  const filtered = tickets.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.title?.toLowerCase().includes(q) ||
        t.submitted_by_name?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <ManagerLayout manager={manager}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E293B' }}>
          Support Tickets
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 4 }}>
          Manage and respond to company support requests
        </p>
      </div>

      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 200px)' }}>
        {/* Left: ticket list */}
        <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Search & filter */}
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{
              position: 'absolute', left: 10,
              top: '50%', transform: 'translateY(-50%)', color: '#94A3B8',
            }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tickets..."
              style={{
                width: '100%', padding: '9px 12px 9px 32px',
                border: '1.5px solid #E2E8F0', borderRadius: 10,
                fontSize: 13, outline: 'none',
              }} />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {[{ v: 'all', l: 'All', c: '#4F8EF7' }, ...STATUS_OPTIONS].map(s => (
              <button key={s.v}
                onClick={() => setStatusFilter(s.v)}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer',
                  border: statusFilter === s.v ? `1.5px solid ${s.c}` : '1.5px solid #E2E8F0',
                  background: statusFilter === s.v ? `${s.c}15` : '#fff',
                  color: statusFilter === s.v ? s.c : '#64748B',
                }}>
                {s.l}
              </button>
            ))}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 13 }}>
                No tickets found
              </div>
            ) : filtered.map(t => {
              const statusOpt = STATUS_OPTIONS.find(s => s.v === t.status);
              const isSelected = selected?.id === t.id;
              const isUrgent = t.priority === 'urgent';
              return (
                <div key={t.id} onClick={() => openTicket(t)} style={{
                  background: isSelected
                    ? 'linear-gradient(135deg,rgba(79,142,247,0.06),rgba(124,58,237,0.04))'
                    : '#fff',
                  borderRadius: 12, padding: 14, cursor: 'pointer',
                  border: isSelected ? '1.5px solid rgba(79,142,247,0.3)' : '1.5px solid #E2E8F0',
                  borderLeft: isUrgent ? '4px solid #EF4444' : undefined,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B', flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>
                      {t.title}
                    </div>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                      background: `${statusOpt?.c || '#94A3B8'}15`,
                      color: statusOpt?.c || '#94A3B8', flexShrink: 0,
                    }}>
                      {statusOpt?.l || t.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>
                    {t.submitted_by_name}
                    {isUrgent && <span style={{ color: '#EF4444', fontWeight: 700 }}> · 🚨 URGENT</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#CBD5E1', marginTop: 3 }}>
                    {new Date(t.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric',
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: ticket detail */}
        <div style={{
          flex: 1, background: '#fff', borderRadius: 14,
          border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {!selected ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#94A3B8', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ fontSize: 40 }}>🎫</div>
              <div style={{ fontWeight: 600, color: '#64748B' }}>
                Select a ticket to view and respond
              </div>
            </div>
          ) : (
            <>
              {/* Ticket header */}
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#1E293B' }}>
                    {selected.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 3 }}>
                    From: {selected.submitted_by_name} ({selected.submitted_by_email})
                    {' · '}
                    Priority: <span style={{
                      color: selected.priority === 'urgent' ? '#EF4444'
                        : selected.priority === 'high' ? '#F59E0B' : '#64748B',
                      fontWeight: 700, textTransform: 'uppercase',
                    }}>{selected.priority}</span>
                  </div>
                </div>
                {/* Status selector */}
                <select
                  value={selected.status}
                  onChange={e => updateTicketStatus(selected.id, e.target.value)}
                  style={{
                    padding: '6px 10px', border: '1.5px solid #E2E8F0',
                    borderRadius: 8, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', outline: 'none',
                  }}>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.v} value={s.v}>{s.l}</option>
                  ))}
                </select>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
                {messages.map(msg => (
                  <div key={msg.id} style={{
                    display: 'flex',
                    justifyContent: msg.sender_type === 'manager' ? 'flex-end' : 'flex-start',
                    marginBottom: 16,
                  }}>
                    <div style={{
                      maxWidth: '70%',
                      background: msg.sender_type === 'manager'
                        ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)'
                        : '#F8FAFF',
                      color: msg.sender_type === 'manager' ? '#fff' : '#1E293B',
                      borderRadius: msg.sender_type === 'manager'
                        ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      padding: '12px 16px',
                      border: msg.sender_type === 'manager' ? 'none' : '1px solid #E2E8F0',
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, marginBottom: 6,
                        color: msg.sender_type === 'manager'
                          ? 'rgba(255,255,255,0.7)' : '#94A3B8',
                      }}>
                        {msg.sender_name} · {new Date(msg.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.6 }}>{msg.message}</div>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 13, padding: 20 }}>
                    No messages yet. Send your first response below.
                  </div>
                )}
              </div>

              {/* Reply box */}
              {!['resolved', 'closed'].includes(selected.status) && (
                <div style={{
                  padding: '14px 20px', borderTop: '1px solid #F1F5F9',
                  display: 'flex', gap: 10,
                }}>
                  <textarea
                    value={reply}
                    onChange={e => setReply(e.target.value)}
                    placeholder="Type your response to the company..."
                    rows={3}
                    style={{
                      flex: 1, padding: '10px 14px',
                      border: '1.5px solid #E2E8F0', borderRadius: 10,
                      fontSize: 13, resize: 'none', outline: 'none',
                    }}
                  />
                  <button onClick={sendReply} disabled={!reply.trim() || sending}
                    style={{
                      padding: '10px 16px', borderRadius: 10, border: 'none',
                      background: reply.trim()
                        ? 'linear-gradient(135deg,#4F8EF7,#7C3AED)' : '#E2E8F0',
                      color: reply.trim() ? '#fff' : '#94A3B8',
                      cursor: reply.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontWeight: 700, fontSize: 13,
                    }}>
                    <Send size={14} />
                    {sending ? 'Sending...' : 'Reply'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ManagerLayout>
  );
}